import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Lightbulb, Store, Layers, Presentation, Users, Zap, Search, ChevronLeft, ChevronRight, MapPin, Calendar, Quote, ChevronDown } from 'lucide-react';
import sorodotePhoto from '../assets/soro-dote.jpg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero — when no carousel image is configured in admin, the section falls back to
// the institutional brand background color (#1E3A5F) without any external image.
// This avoids displaying Western/non-Ivorian stock visuals on the landing.
// Admin should configure the carousel from /admin/parametres with actual URBANIK photos.

const HeroCarousel = ({ slides }) => {
  const [index, setIndex] = useState(0);
  const total = slides.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (total <= 1) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, total]);

  return (
    <section className="relative h-[88vh] sm:h-screen overflow-hidden bg-[#1E3A5F]" data-testid="hero-carousel">
      {/* Subtle architectural texture using brand colors — pure CSS, no external imagery */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(245,166,35,0.18), transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.10), transparent 50%)',
        }}
      />
      {slides.map((s, i) => (
        s.image_url ? (
          <div
            key={s.id || s.image_url || `slide-${i}`}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            data-testid={`hero-slide-${i}`}
          >
            <img
              src={s.image_url}
              alt={s.alt || 'URBANIK 2026 — Forum de l\'Innovation et des Technologies du BTP'}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                // If the configured image fails, hide it and fall back to brand background.
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        ) : null
      ))}

      {/* Subtle dark gradient at bottom for content legibility — does not distort the image */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 via-black/20 to-transparent pointer-events-none z-10" aria-hidden="true" />

      {/* "Édition 2026" pill — top left, institutional accent */}
      <div className="absolute top-24 sm:top-28 left-4 sm:left-10 z-20" data-testid="hero-edition-pill">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/95 backdrop-blur text-[#1E3A5F] text-xs font-bold uppercase tracking-[0.18em] shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F5A623]" />
          Édition 2026
        </span>
      </div>

      {/* Premium info card — glassmorphism, bottom-left */}
      <div className="absolute z-20 left-4 right-4 sm:left-10 sm:right-auto bottom-24 sm:bottom-28 sm:max-w-xl" data-testid="hero-info-card">
        <div className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md shadow-2xl px-5 py-5 sm:px-7 sm:py-7">
          <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
            URBANIK <span className="text-[#F5A623]">2026</span>
          </h1>
          <p className="mt-2 sm:mt-3 text-white/90 text-sm sm:text-base font-medium leading-snug">
            Forum du contenu local dans l'industrie du BTP ivoirien et africain
          </p>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-white/85 text-xs sm:text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#F5A623] flex-shrink-0" />
              <span>14 — 16 Septembre 2026</span>
            </div>
            <span className="hidden sm:inline text-white/30">·</span>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#F5A623] flex-shrink-0" />
              <span>SOFITEL Hôtel Ivoire, Abidjan</span>
            </div>
          </div>
          <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row gap-2.5">
            <Link
              to="/participer#inscription"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#F5A623] hover:bg-[#E09615] text-[#1E3A5F] text-sm font-semibold transition-all shadow-md hover:shadow-lg group"
              data-testid="hero-register-btn"
            >
              S'inscrire
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/programme"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-white/40 hover:bg-white/10 text-white text-sm font-semibold transition-all"
              data-testid="hero-program-btn"
            >
              Voir le programme
            </Link>
          </div>
        </div>
      </div>

      {/* Carousel controls — only when multiple slides */}
      {total > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Slide précédent"
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/30 backdrop-blur hover:bg-black/50 border border-white/30 text-white flex items-center justify-center transition-all"
            data-testid="hero-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            aria-label="Slide suivant"
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/30 backdrop-blur hover:bg-black/50 border border-white/30 text-white flex items-center justify-center transition-all"
            data-testid="hero-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-8 sm:bottom-12 right-4 sm:right-10 z-20 flex gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id || s.image_url || `dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Aller au slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-10 bg-[#F5A623]' : 'w-5 bg-white/50 hover:bg-white/80'}`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Premium transition strip — soft white fade at the very bottom */}
      <div className="absolute inset-x-0 bottom-0 h-12 sm:h-16 bg-gradient-to-b from-transparent to-white pointer-events-none z-10" aria-hidden="true" />
    </section>
  );
};

const PRESIDENT_DEFAULTS = {
  enabled: true,
  name: 'SORO Doté',
  function_line_1: "Président du Comité d'Organisation d'URBANIK 2026",
  function_line_2: 'Président de la Confédération des PME/PMI du BTP de Côte d\'Ivoire',
  title: 'Mot du Président du Comité d\'Organisation',
  // Bundled official photo of President SORO Doté — fallback when admin hasn't uploaded one.
  photo_url: sorodotePhoto,
  message: `L'Afrique construit aujourd'hui les fondations de son avenir. Dans cette dynamique, le secteur du BTP représente un levier stratégique de croissance, d'emploi et de transformation économique.

Avec URBANIK 2026, nous avons choisi de placer au cœur des débats la question du contenu local dans l'industrie du BTP ivoirien et africain, parce qu'il est essentiel que nos entreprises, nos compétences, nos innovations et nos matériaux participent pleinement à la construction de nos villes et infrastructures.

Cette édition se veut ambitieuse, inclusive et résolument tournée vers l'action. Elle offrira un espace unique de dialogue entre les pouvoirs publics, les investisseurs, les institutions, les experts et les PME afin de bâtir ensemble des solutions concrètes pour renforcer la souveraineté économique et technologique de notre secteur.

URBANIK 2026 sera également une vitrine de l'excellence africaine, de l'innovation et des opportunités de partenariat dans un environnement marqué par les enjeux de compétitivité, de digitalisation et de transition écologique.

J'invite l'ensemble des acteurs du BTP, des industries connexes, des institutions financières, des organisations professionnelles et des partenaires internationaux à prendre une part active à cette édition exceptionnelle. Ensemble, faisons du contenu local un moteur durable de développement pour l'Afrique.`,
  cta_label: 'Découvrir le forum',
  cta_link: '/evenement#presentation',
};

const MotPresidentSection = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  if (!data || data.enabled === false) return null;
  const paragraphs = (data.message || '').split(/\n\s*\n/).filter(Boolean);
  const isLong = paragraphs.length > 2;
  const visibleParagraphs = expanded || !isLong ? paragraphs : paragraphs.slice(0, 2);

  return (
    <section id="mot-president" className="py-20 lg:py-28 bg-white" data-testid="mot-president-section">
      <div className="container-institutional">
        {/* Section header */}
        <div className="text-center mb-10 lg:mb-14">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Édito officiel</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F]">{data.title || PRESIDENT_DEFAULTS.title}</h2>
          <div className="mt-4 h-1 w-16 bg-[#F5A623] mx-auto rounded-full" aria-hidden="true" />
        </div>

        {/* Unified editorial card — photo + identity overlay + quote + signature/CTA footer */}
        <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-[#F7F9FC] to-white border border-[#E1E8F0] shadow-[0_4px_30px_rgba(30,58,95,0.08)]">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Photo column — flush to card edges, identity card overlay at bottom */}
            <div className="lg:col-span-5 relative bg-[#1E3A5F]/5">
              <div className="aspect-[4/5] lg:aspect-auto lg:h-full lg:min-h-[520px]">
                {data.photo_url ? (
                  <img
                    src={data.photo_url}
                    alt={`${data.name} — ${data.function_line_1}`}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#94A3B8] text-sm italic px-6 text-center">
                    Photo officielle à uploader<br />depuis l'admin
                  </div>
                )}
              </div>
              {/* Identity card — overlaid at the bottom of the photo, tied visually to the portrait */}
              <div className="absolute left-4 right-4 bottom-4 sm:left-6 sm:right-6 sm:bottom-6 bg-white/95 backdrop-blur rounded-xl px-4 py-3 sm:px-5 sm:py-4 border border-white shadow-lg">
                <p className="text-base sm:text-lg font-bold text-[#1E3A5F]" data-testid="president-name">{data.name}</p>
                <p className="text-xs sm:text-sm text-[#4A5568] leading-snug mt-1">{data.function_line_1}</p>
                {data.function_line_2 && (
                  <p className="text-xs sm:text-sm text-[#4A5568] leading-snug mt-0.5">{data.function_line_2}</p>
                )}
              </div>
            </div>

            {/* Quote column */}
            <div className="lg:col-span-7 p-6 sm:p-10 lg:p-14 flex flex-col">
              <Quote className="w-10 h-10 text-[#F5A623]/40 mb-4 flex-shrink-0" aria-hidden="true" />
              <div className="space-y-4 text-[#4A5568] text-base sm:text-[17px] leading-relaxed">
                {visibleParagraphs.map((p, i) => (
                  <p key={`pres-p-${i}-${p.slice(0, 20)}`}>{p}</p>
                ))}
              </div>
              {isLong && (
                <button
                  type="button"
                  onClick={() => setExpanded(v => !v)}
                  className="mt-5 self-start inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A5F] hover:text-[#F5A623] transition-colors"
                  data-testid="president-toggle"
                >
                  {expanded ? 'Réduire' : 'Lire la suite'}
                  <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </button>
              )}

              {/* Signature + CTA footer — tied together with a divider, clearly framed */}
              <div className="mt-8 pt-6 border-t border-[#E1E8F0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-[#1E3A5F] font-semibold italic">— {data.name}</p>
                {data.cta_label && data.cta_link && (
                  <Link
                    to={data.cta_link}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A5F] hover:bg-[#152A45] text-white text-sm font-semibold transition-all shadow-md hover:shadow-lg group self-start sm:self-auto"
                    data-testid="president-cta"
                  >
                    {data.cta_label}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Countdown = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const calc = () => {
      const diff = targetDate - new Date();
      if (diff > 0) setTimeLeft({ days: Math.floor(diff / 86400000), hours: Math.floor((diff / 3600000) % 24), minutes: Math.floor((diff / 60000) % 60), seconds: Math.floor((diff / 1000) % 60) });
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  const f = (n) => String(n).padStart(2, '0');
  return (
    <div className="flex items-center gap-3" data-testid="countdown-timer">
      {[{ v: timeLeft.days, l: 'Jours' }, { v: timeLeft.hours, l: 'Heures' }, { v: timeLeft.minutes, l: 'Min' }, { v: timeLeft.seconds, l: 'Sec' }].map((item, i) => (
        <React.Fragment key={item.l}>{i > 0 && <span className="text-lg text-white/30">:</span>}
          <div className="countdown-box"><span className="countdown-value">{f(item.v)}</span><span className="countdown-label">{item.l}</span></div>
        </React.Fragment>
      ))}
    </div>
  );
};

const Home = ({ onOpenForumModal }) => {
  const [eventDate, setEventDate] = useState(new Date('2026-09-14T09:00:00'));
  const [speakers, setSpeakers] = useState([]);
  // const [exhibitors, setExhibitors] = useState([]); // MASQUÉ: remplacé par "partners" sur la home
  const [partners, setPartners] = useState([]);
  const [program, setProgram] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [president, setPresident] = useState(PRESIDENT_DEFAULTS);

  useEffect(() => {
    const init = async () => {
      try {
        await axios.post(`${API}/seed`).catch(() => {});
        const [st, sp, pt, pr] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/intervenants`),
          axios.get(`${API}/partenaires`),
          axios.get(`${API}/program`),
        ]);
        if (st.data.event_date) setEventDate(new Date(st.data.event_date));
        // Load hero carousel slides (fallback to default if empty)
        const carousel = Array.isArray(st.data.hero_carousel) ? st.data.hero_carousel : [];
        setHeroSlides(carousel);
        // Load Mot du Président — supports object (settings.president) or legacy individual fields
        const cfg = st.data.president && typeof st.data.president === 'object' ? st.data.president : {};
        const legacy = {
          name: st.data.president_name,
          // Ignore the legacy Unsplash placeholder (Western stock seeded during demo) —
          // the bundled official photo of SORO Doté is the canonical fallback.
          photo_url: st.data.president_photo && !st.data.president_photo.includes('unsplash.com') ? st.data.president_photo : null,
          message: st.data.president_quote,
          function_line_1: st.data.president_role,
        };
        const merged = { ...PRESIDENT_DEFAULTS };
        Object.entries(legacy).forEach(([k, v]) => { if (v) merged[k] = v; });
        Object.entries(cfg).forEach(([k, v]) => {
          if (v === undefined || v === null || v === '') return;
          // For photo_url specifically, also reject the Western stock placeholder if it slipped in
          if (k === 'photo_url' && typeof v === 'string' && v.includes('unsplash.com/photo-1560250097-0b93528c311a')) return;
          merged[k] = v;
        });
        setPresident(merged);
        setSpeakers(sp.data.slice(0, 4));
        setPartners(pt.data.slice(0, 10));
        setProgram(pr.data.slice(0, 3));
      } catch (e) { logger.error(e); }
    };
    init();
  }, []);

  return (
    <div className="min-h-screen">
      {/* HERO CAROUSEL — pure image slider (titre/CTA/countdown supprimés) */}
      <HeroCarousel slides={heroSlides} />

      {/* HERO STATIC (MASQUÉ - remplacé par HeroCarousel ci-dessus)
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 container-institutional py-32">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-white/70 mb-8">
              <MapPin className="w-4 h-4" /><span className="text-sm">Palais des Congrès du Sofitel Hôtel Ivoire, Abidjan</span>
              <span className="mx-2 text-white/30">|</span>
              <Calendar className="w-4 h-4" /><span className="text-sm">14, 15 & 16 Septembre 2026</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-3 leading-[1.1]">URBANIK <span className="text-[#F5A623]">2026</span></h1>
            <p className="text-lg text-white/70 mb-3 font-medium">Forum de l'Innovation & des Technologies du BTP</p>
            <p className="text-base text-white/50 mb-10 max-w-lg leading-relaxed">L'innovation technologique dans l'industrie du BTP et son impact sur la consommation de matériaux de construction à l'ère de la digitalisation.</p>
            <div className="mb-10"><Countdown targetDate={eventDate} /></div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/participer" className="btn-primary text-base px-8 py-4" data-testid="hero-participate-btn">Participer <ArrowRight className="w-5 h-5 ml-2" /></Link>
              <Link to="/exposition/devenir-exposant" className="btn-outline border-white text-white hover:bg-white hover:text-[#1E3A5F] text-base px-8 py-4" data-testid="hero-exposant-btn">Devenir Exposant</Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY ATTEND — overlaps Hero bottom for premium "card emerging" transition */}
      <section className="relative z-20 -mt-8 sm:-mt-12 py-24 lg:py-32 bg-white rounded-t-[28px] sm:rounded-t-[36px] shadow-[0_-12px_40px_rgba(30,58,95,0.10)]" data-testid="why-attend-section">
        <div className="container-institutional">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Pourquoi participer ?</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F]">3 jours d'opportunités</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Presentation, title: 'Conférences', desc: 'Panels de haut niveau avec experts internationaux, keynotes et tables rondes sur les enjeux du BTP.' },
              { icon: Users, title: 'Networking', desc: 'Rencontres B2B, échanges directs avec décideurs, investisseurs et professionnels du secteur.' },
              { icon: Zap, title: 'Innovation', desc: 'Découverte de solutions technologiques, démonstrations live, pitch de startups et remise d\'Awards.' },
            ].map((item, i) => (
              <div key={item.title} className="p-8 bg-white border border-[#E1E8F0] rounded-lg text-center hover:shadow-md transition-shadow" data-testid={`why-card-${i}`}>
                <div className="w-14 h-14 rounded-full bg-[#1E3A5F]/5 flex items-center justify-center mx-auto mb-5"><item.icon className="w-6 h-6 text-[#1E3A5F]" /></div>
                <h3 className="text-lg font-bold text-[#1E3A5F] mb-3">{item.title}</h3>
                <p className="text-sm text-[#6B7C93] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOT DU PRÉSIDENT */}
      <MotPresidentSection data={president} />

      {/* PARTNERS PREVIEW (remplace la section Exposants — temporaire) */}
      {partners.length > 0 && (
        <section className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="partners-preview">
          <div className="container-institutional">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Partenaires</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F]">Ils nous font confiance</h2>
              </div>
              <Link to="/partenaires" className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#1E3A5F] hover:text-[#F5A623] transition-colors">
                Voir tous <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 items-stretch">
              {partners.map((p) => (
                <div key={p.id} className="bg-white border border-[#E1E8F0] rounded-lg p-4 flex items-center justify-center hover:shadow-md hover:border-[#F5A623]/40 transition-all h-40 sm:h-44" data-testid={`partner-preview-${p.id}`}>
                  <img src={p.logo_url || 'https://placehold.co/200x100/E1E8F0/6B7C93?text=Logo'} alt={p.name} className="max-h-32 sm:max-h-36 max-w-full w-auto object-contain" title={p.name} />
                </div>
              ))}
            </div>
            <div className="sm:hidden text-center mt-8">
              <Link to="/partenaires" className="btn-outline text-sm px-6 py-3">Voir tous les partenaires</Link>
            </div>
          </div>
        </section>
      )}

      {/* EXHIBITORS PREVIEW (MASQUÉ TEMPORAIREMENT - remplacé par Partenaires ci-dessus)
      {exhibitors.length > 0 && (
        <section className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="exhibitors-preview">
          <div className="container-institutional">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Exposition</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F]">Nos Exposants</h2>
              </div>
              <Link to="/exposition/repertoire" className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#1E3A5F] hover:text-[#F5A623] transition-colors">
                Voir tous <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {exhibitors.map((ex) => (
                <Link key={ex.id} to={`/exposition/repertoire/${ex.id}`} className="bg-white border border-[#E1E8F0] rounded-lg p-6 flex flex-col items-center text-center hover:shadow-md transition-shadow" data-testid={`exhibitor-preview-${ex.id}`}>
                  <img src={ex.logo_url || 'https://placehold.co/120x50/E1E8F0/6B7C93?text=Logo'} alt={ex.company_name} className="h-10 w-auto mb-4" />
                  <p className="text-sm font-semibold text-[#1E3A5F]">{ex.company_name}</p>
                  <p className="text-xs text-[#94A3B8] mt-1 capitalize">{ex.sector}</p>
                </Link>
              ))}
            </div>
            <div className="sm:hidden text-center mt-8">
              <Link to="/exposition/repertoire" className="btn-outline text-sm px-6 py-3">Voir tous les exposants</Link>
            </div>
          </div>
        </section>
      )}
      */}

      {/* PROGRAM HIGHLIGHTS */}
      {program.length > 0 && (
        <section className="py-20 lg:py-28" data-testid="program-highlights">
          <div className="container-institutional">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Programme</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F]">Sessions à venir</h2>
              </div>
              <Link to="/programme" className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#1E3A5F] hover:text-[#F5A623] transition-colors">Programme complet <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {program.map((p) => (
                <div key={p.id} className="p-6 bg-white border border-[#E1E8F0] rounded-lg hover:shadow-md transition-shadow" data-testid={`program-card-${p.id}`}>
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#F5A623]">{p.type}</span>
                  <h3 className="text-base font-bold text-[#1E3A5F] mt-2 mb-2">{p.title}</h3>
                  <p className="text-sm text-[#6B7C93] mb-3">{p.description}</p>
                  <div className="flex items-center gap-3 text-xs text-[#94A3B8]">
                    <span>{p.time}</span>{p.location && <><span>|</span><span>{p.location}</span></>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPEAKERS (MASQUÉ TEMPORAIREMENT - en attente des contenus finaux)
      {speakers.length > 0 && (
        <section className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="speakers-preview">
          <div className="container-institutional">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Intervenants</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F]">Nos Speakers</h2>
              </div>
              <Link to="/intervenants" className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#1E3A5F] hover:text-[#F5A623] transition-colors">Tous les speakers <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {speakers.map((s) => (
                <Link key={s.id} to={`/intervenants/${s.id}`} className="bg-white border border-[#E1E8F0] rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-testid={`speaker-card-${s.id}`}>
                  <img src={s.photo_url} alt={s.name} className="w-full aspect-square object-cover" />
                  <div className="p-4"><p className="font-bold text-sm text-[#1E3A5F]">{s.name}</p><p className="text-xs text-[#6B7C93] mt-1">{s.role}</p></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      */}

      {/* BECOME EXHIBITOR CTA */}
      <section className="py-20 lg:py-28 bg-[#1E3A5F]" data-testid="cta-exhibitor">
        <div className="container-institutional text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Exposition</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Devenez Exposant</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">Présentez vos innovations aux décideurs et professionnels du secteur du BTP. Réservez votre stand dès maintenant.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/exposition/devenir-exposant" className="btn-primary text-base px-8 py-4" data-testid="cta-reserve-btn">Réserver mon Stand <ArrowRight className="w-5 h-5 ml-2" /></Link>
            <Link to="/exposition/stands" className="btn-outline border-white text-white hover:bg-white hover:text-[#1E3A5F] text-base px-8 py-4" data-testid="cta-plan-btn"><Search className="w-5 h-5 mr-2" /> Plan d'exposition</Link>
          </div>
        </div>
      </section>

      {/* STRATEGIC ROLES */}
      <section className="py-24 lg:py-32" data-testid="roles-section">
        <div className="container-institutional">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Eye, title: 'Vitrine', desc: 'Solutions innovantes dans les services, technologies et ingénieries du BTP' },
              { icon: Lightbulb, title: 'Innovation', desc: 'Pôle valorisant des projets à fort potentiel et initiatives prometteuses' },
              { icon: Store, title: 'Marché', desc: 'Place de marché numérique assurant une continuité avant et après l\'événement' },
              { icon: Layers, title: 'Fertilisation', desc: 'Espace de fertilisation croisée entre acteurs du secteur et acteurs périphériques' },
            ].map((r, i) => (
              <div key={r.title} className="text-center" data-testid={`role-card-${i}`}>
                <div className="w-14 h-14 rounded-full bg-[#1E3A5F]/5 flex items-center justify-center mx-auto mb-5"><r.icon className="w-6 h-6 text-[#1E3A5F]" /></div>
                <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">{r.title}</h3>
                <p className="text-sm text-[#6B7C93] leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
